import ExcelJS from 'exceljs';
import { formatEtDate } from './formatEt';
import { deltaView, fmtCount } from './analyticsMetrics';
import { todayEastern } from './dateUtils';
import { analyticsUiTokens as T } from '../constants/userAnalytics';

// The Excel export comes from ONE row model (buildMetricsRows), built from the numbers
// already on screen — no extra request.

export const REPORT_TITLE = 'TifoXR CMS — User Analytics Report';
const ET_NOTE = 'All dates and day boundaries are US Eastern (ET).';

const previousNote = (point) =>
  point && typeof point.previous === 'number'
    ? ` Previous period: ${fmtCount(point.previous)} (${deltaView(point.deltaPct).text}).`
    : '';

/**
 * @param {object|null} summary    GET /summary response
 * @param {{entity?: string|null, space?: string|null, domain?: string|null,
 *          restricted?: boolean}} filters  display text, not request params. There is no date
 *          filter on the page, so the report carries no "Date Period" either.
 * @param {boolean} showBreakdown  Immersive/CMS rows are exported only while the toggle is on
 */
export const buildMetricsRows = (summary, filters, showBreakdown) => {
  const totals = summary?.totals;
  const active = summary?.active;
  const asOf = active?.asOf ? formatEtDate(active.asOf) : 'the end of the period';
  const immersiveOnly = summary?.capabilities?.cmsActivityTracked === false ? ' Immersive sessions only.' : '';

  const overview = [
    {
      metric: 'Total Users',
      value: totals?.total ?? null,
      unit: 'Users',
      notes: totals?.both > 0
        ? `Distinct people matching the selected filters (${fmtCount(totals.both)} have both CMS and Immersive accounts).`
        : 'Distinct people matching the selected filters.',
    },
  ];
  if (showBreakdown) {
    overview.push(
      { metric: 'Total Immersive Users', value: totals?.immersive ?? null, unit: '3D / VR Users', notes: 'Users who signed in to 3D Immersive spaces.', indent: true },
      { metric: 'Total CMS Users', value: totals?.cms ?? null, unit: 'CMS Members', notes: 'CMS portal members who have signed in.', indent: true },
    );
  }
  overview.push(
    {
      metric: 'Daily Active Users (DAU)',
      value: active?.dau?.value ?? null,
      unit: 'Active Users',
      notes: `Unique users active on ${asOf} (ET).${immersiveOnly}${previousNote(active?.dau)}`,
    },
    {
      metric: 'Weekly Active Users (WAU)',
      value: active?.wau?.value ?? null,
      unit: 'Active Users',
      notes: `Unique users active in the 7 days ending ${asOf} (ET).${immersiveOnly}${previousNote(active?.wau)}`,
    },
    {
      metric: 'Monthly Active Users (MAU)',
      value: active?.mau?.value ?? null,
      unit: 'Active Users',
      notes: `Unique users active in the 30 days ending ${asOf} (ET).${immersiveOnly}${previousNote(active?.mau)}`,
    },
  );

  const meta = filters.restricted
    ? [['Space Filter', filters.space || '—']]
    : [
      ['Entity Filter', filters.entity || 'All Entities'],
      ['Space Filter', filters.space || 'All Spaces'],
      ['Email Domain', filters.domain ? `@${filters.domain}` : 'All Email Domains'],
    ];

  return {
    title: REPORT_TITLE,
    subtitle: `Exported from TifoXR User Analytics (includes active view filters and toggles). ${ET_NOTE}`,
    meta,
    sections: [
      {
        title: '1. USERS & GROWTH OVERVIEW',
        category: 'Users & Growth',
        header: ['Metric Name', 'Value', 'Category / Unit', 'Description & Calculation Notes'],
        rows: overview,
      },
    ],
  };
};

// ── Excel (.xlsx) ────────────────────────────────────────────────────────────
// Labels (space / entity / domain) are user-controlled. ExcelJS writes a plain string as a text
// cell, so one that starts with "=" is shown as text and never evaluated; do not pass them as
// `{ formula }` objects.

const argb = (hex) => `FF${hex.replace('#', '').toUpperCase()}`;
const BORDER = { style: 'thin', color: { argb: argb(T.slate[300]) } };
const CELL_BORDER = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: argb(hex) } });

export const XLSX_COLUMN_WIDTHS = [36, 18, 22, 54];
export const XLSX_COUNT_FORMAT = '#,##0';

export const buildWorkbook = (report) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GMS Content Management';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('TifoXR User Analytics');
  sheet.columns = XLSX_COLUMN_WIDTHS.map((width) => ({ width }));

  const banner = (text, { size, fill, color, bold, height }) => {
    const row = sheet.addRow([text]);
    sheet.mergeCells(row.number, 1, row.number, 4);
    const cell = row.getCell(1);
    cell.value = text;
    cell.font = { bold, size, color: { argb: argb(color) } };
    cell.fill = solid(fill);
    cell.alignment = { vertical: 'middle', wrapText: true };
    row.height = height;
    return row;
  };

  banner(report.title, { size: 15, fill: T.navy, color: '#ffffff', bold: true, height: 31.5 });
  banner(report.subtitle, { size: 9.5, fill: T.activeNav, color: T.slate[200], bold: false, height: 19.5 });

  for (let i = 0; i < report.meta.length; i += 2) {
    const pair = report.meta.slice(i, i + 2);
    const row = sheet.addRow([pair[0][0], pair[0][1], pair[1]?.[0] ?? '', pair[1]?.[1] ?? '']);
    row.height = 21;
    [1, 3].forEach((col) => {
      const label = row.getCell(col);
      label.font = { bold: true, size: 9, color: { argb: argb(T.slate[600]) } };
      label.fill = solid(T.slate[100]);
      label.border = CELL_BORDER;
      label.alignment = { vertical: 'middle' };
      const val = row.getCell(col + 1);
      val.font = { bold: true, size: 9.5, color: { argb: argb(T.navy) } };
      val.border = CELL_BORDER;
      val.alignment = { vertical: 'middle', wrapText: true };
    });
  }

  report.sections.forEach((section) => {
    sheet.addRow([]).height = 12;
    banner(section.title, { size: 10.5, fill: T.slate[200], color: T.navy, bold: true, height: 24 });

    const head = sheet.addRow(section.header);
    head.height = 22.5;
    head.eachCell((cell, col) => {
      cell.font = { bold: true, size: 9.5, color: { argb: argb('#ffffff') } };
      cell.fill = solid(T.navy);
      cell.border = CELL_BORDER;
      cell.alignment = { vertical: 'middle', horizontal: col === 2 ? 'right' : col === 3 ? 'center' : 'left' };
    });

    section.rows.forEach((r) => {
      const row = sheet.addRow([r.metric, r.value == null ? '—' : r.value, r.unit, r.notes]);
      row.height = Math.max(22.5, 15 * Math.ceil(String(r.notes ?? '').length / 60));
      row.eachCell((cell) => { cell.border = CELL_BORDER; cell.alignment = { vertical: 'middle', wrapText: true }; });

      const [metric, value, unit, notes] = [1, 2, 3, 4].map((c) => row.getCell(c));
      metric.font = { bold: true, size: 10, color: { argb: argb(T.slate[800]) } };
      if (r.indent) metric.alignment = { vertical: 'middle', wrapText: true, indent: 1 };
      value.font = { bold: true, size: 11, color: { argb: argb(T.navy) } };
      value.alignment = { vertical: 'middle', horizontal: 'right' };
      if (typeof r.value === 'number') value.numFmt = XLSX_COUNT_FORMAT;
      unit.font = { size: 9.5, color: { argb: argb(T.slate[600]) } };
      unit.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      notes.font = { size: 9.5, color: { argb: argb(T.slate[600]) } };
    });
  });

  return workbook;
};

// ── Delivery ─────────────────────────────────────────────────────────────────

// The date in the name is the day of the export (ET), not a data filter.
export const exportFilename = (ext) => `TifoXR_User_Analytics_${todayEastern()}.${ext}`;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportXlsx = async (report) => {
  const buffer = await buildWorkbook(report).xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    exportFilename('xlsx'),
  );
};
